// =============================================================================
// BOOT SEQUENCE LANDING - Захоплива точка входу з boot анімацією
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';

interface BootSequenceLandingProps {
  onStartGame: () => void;
  onContinueGame: () => void;
  onCreateGame: () => void;
  onViewRules: () => void;
  hasSavedGame: boolean;
}

const BOOT_MESSAGES = [
  { text: 'ІНІЦІАЛІЗАЦІЯ СИСТЕМИ...', type: 'system' },
  { text: 'СТАТУС: 20 ЛЮТОГО 2022, 06:00', type: 'info' },

  {
    text: 'ПОПЕРЕДЖЕННЯ: КОНЦЕНТРАЦІЯ РОСІЙСЬКИХ ВІЙСЬК БІЛЯ КОРДОНУ',
    type: 'warning',
  },
  { text: 'ОЧІКУВАННЯ КОМАНДУВАЧА...', type: 'await' },
];

export function BootSequenceLanding({
  onStartGame,
  onContinueGame,
  onCreateGame,
  onViewRules,
  hasSavedGame,
}: Readonly<BootSequenceLandingProps>) {
  const [, setBootComplete] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState<typeof BOOT_MESSAGES>(
    [],
  );
  const [showContent, setShowContent] = useState(false);
  const terminalRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        setBootComplete(true);
        setTimeout(() => setShowContent(true), 300);
      },
    });

    // Terminal messages
    BOOT_MESSAGES.forEach((message, index) => {
      tl.call(
        () => {
          setVisibleMessages((prev) => [...prev, message]);
          if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
          }
        },
        [],
        index * 0.6,
      );
    });

    // Progress bar
    tl.to(
      {},
      {
        duration: 2.5,
        onUpdate: function () {
          setCurrentProgress(Math.floor(this.progress() * 100));
        },
      },
      1,
    );

    if (progressBarRef.current) {
      tl.to(
        progressBarRef.current,
        { width: '100%', duration: 2.5, ease: 'power2.inOut' },
        1,
      );
    }

    return () => {
      tl.kill();
    };
  }, []);

  const getMessageColor = (type: string) => {
    switch (type) {
      case 'warning':
        return 'text-war-amber';
      case 'await':
        return 'text-war-bright animate-pulse';
      case 'info':
        return 'text-war-primary/70';
      default:
        return 'text-war-primary';
    }
  };

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center px-4 py-6 relative"
      role="main"
      aria-label="Головнокомандувач"
    >
      <div className="relative z-10 w-full max-w-2xl">
        {/* Compact Terminal */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 mb-4"
        >
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-war-dim/20">
            <div className="w-2 h-2 rounded-full bg-war-red/70" />
            <div className="w-2 h-2 rounded-full bg-war-amber/70" />
            <div className="w-2 h-2 rounded-full bg-war-primary/70" />
            <span className="ml-2 text-war-dim text-[9px] tracking-wider uppercase">
              СИСТЕМА УПРАВЛІННЯ
            </span>
          </div>

          <div
            ref={terminalRef}
            className="font-mono text-xs space-y-1.5 min-h-[80px]"
          >
            {visibleMessages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                className={`flex items-start gap-2 ${getMessageColor(message.type)}`}
              >
                <span className="text-war-dim/50">&gt;</span>
                <span>{message.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Compact Progress */}
          <div className="mt-3 pt-2 border-t border-war-dim/20">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1 bg-war-bg/50 border border-war-dim/30 overflow-hidden">
                <div
                  ref={progressBarRef}
                  className="h-full bg-war-primary"
                  style={{ width: '0%' }}
                />
              </div>
              <span className="text-war-dim text-[10px] font-mono w-8">
                {currentProgress}%
              </span>
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <AnimatePresence>
          {showContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Hero Section */}
              <div className="glass-card p-6 md:p-8 mb-4">
                <div className="text-center mb-3">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    className="flex justify-center mb-3"
                  >
                    <div className="relative">
                      <div className="w-14 h-14 relative opacity-60">
                        <div className="absolute inset-0 bg-war-primary/5 blur-xl rounded-full" />
                        <img
                          src="/logo.png"
                          alt="Logo"
                          className="w-full h-full object-contain filter drop-shadow-[0_0_8px_rgba(16,185,129,0.2)] mix-blend-screen saturate-100"
                        />
                      </div>
                      {/* BETA Badge */}
                      <span className="absolute -top-0.5 -right-6 text-[6px] font-mono font-bold tracking-widest text-war-amber/80 bg-war-amber/10 border border-war-amber/30 px-1 py-0.5 uppercase">
                        BETA
                      </span>
                    </div>
                  </motion.div>
                  <motion.h1
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-2xl md:text-3xl font-bold text-war-bright tracking-wider uppercase mb-2"
                  >
                    Головнокомандувач
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-war-primary/70 text-sm"
                  >
                    Текстовий симулятор рішень та наслідків
                  </motion.p>
                </div>

                {/* System Briefing / Mechanics */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-3 mb-4 py-4 border-y border-war-dim/10"
                >
                  <div className="flex flex-col gap-2 text-left font-mono">
                    <ul className="text-war-primary/70 text-[11px] md:text-xs space-y-1.5 uppercase tracking-wide">
                      <li>• Ви приймаєте рішення як Головнокомандувач</li>
                      <li>• Кожен вибір має незворотні наслідки</li>
                      <li>
                        • Можливість інтеграції реальних зборів та петицій
                      </li>
                    </ul>
                  </div>
                </motion.div>
              </div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col items-center gap-4"
              >
                {/* Continue Game Button (if saved game exists) */}
                {hasSavedGame && (
                  <button
                    onClick={onContinueGame}
                    className="w-full group relative bg-war-primary/20 border-2 border-war-primary hover:bg-war-primary/30 transition-all py-4 px-6 active:scale-[0.98]"
                  >
                    <span className="text-war-primary group-hover:text-war-bright text-lg font-bold tracking-widest uppercase transition-colors">
                      Відновити командування
                    </span>
                    {/* Corner accents */}
                    <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-war-primary" />
                    <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-war-primary" />
                    <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-war-primary" />
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-war-primary" />
                  </button>
                )}

                {/* Primary CTA */}
                <button
                  onClick={onStartGame}
                  className={`w-full group relative bg-war-primary/10 border-2 border-war-primary hover:bg-war-primary/20 transition-all py-4 px-6 active:scale-[0.98] ${hasSavedGame ? 'opacity-70' : ''
                    }`}
                >
                  <span className="text-war-primary group-hover:text-war-bright text-lg font-bold tracking-widest uppercase transition-colors">
                    {hasSavedGame ? 'Нова кампанія' : 'Прийняти командування'}
                  </span>
                  {/* Corner accents */}
                  <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-war-primary" />
                  <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-war-primary" />
                  <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-war-primary" />
                  <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-war-primary" />
                </button>

                {/* Optional Advanced Action */}
                <button
                  onClick={onCreateGame}
                  className="text-war-amber hover:text-war-amber/80 transition-colors text-[10px] md:text-xs uppercase tracking-widest font-mono border-b border-transparent hover:border-war-amber/30 pb-0.5"
                >
                  Додати власний збір або петицію
                </button>
              </motion.div>

              {/* Footer - Terminal Status Bar */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="mt-6 flex items-center justify-center gap-2 text-[9px] text-war-dim/50 font-mono tracking-wider uppercase"
              >
                <button
                  onClick={onViewRules}
                  className="hover:text-war-dim transition-colors"
                >
                  СТАТУТ
                </button>
                <span className="text-war-dim/30">|</span>
                <span>Канали зв'язку:</span>
                <div className="flex items-center gap-1.5">
                  <a
                    href="https://t.me/zavoruha_tech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [TG]
                  </a>
                  <a
                    href="https://www.instagram.com/zavoruha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [IG]
                  </a>
                  <a
                    href="https://www.threads.com/@zavoruha"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [TR]
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61587632097637"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-war-primary transition-colors"
                  >
                    [FB]
                  </a>
                </div>
                <span className="text-war-dim/30">|</span>
                <span>v0.9 BETA</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
